import type {
  ApolloClient,
  MutationUpdaterFn,
  NormalizedCache,
} from '@apollo/client';

import { config } from 'src/config';
import { defaultLocalStorageAdapter } from 'src/shared/graphql';
import type {
  AuthenticateWithCookieMutation,
  CreateAiGeneratedImageAsyncMutation,
  CreateAiGeneratedImageAsyncMutationVariables,
  CreateAiOriginalImageMutation,
  CreateAiOriginalImageMutationVariables,
  CreateAiOriginalImagePlaceholderMutation,
  CreateAiOriginalImagePlaceholderMutationVariables,
  CreateFileStorageTokenMutation,
  CreateFileStorageTokenMutationVariables,
  CreateHiDefRenderingMutation,
  CreateHiDefRenderingMutationVariables,
  CreateProjectMutation,
  CreateProjectMutationVariables,
  DeleteAiOriginalImageMutation,
  DeleteAiOriginalImageMutationVariables,
  DesignStudioProductDataQuery,
  DesignStudioProductDataQueryVariables,
  DetectUserSessionQuery,
  DetectUserSessionQueryVariables,
  GenericProductSettingsQuery,
  GenericProductSettingsQueryVariables,
  GetJobQuery,
  GetJobQueryVariables,
  GetProductsQuery,
  GetProductsQueryVariables,
  GetRenderingsQuery,
  GetRenderingsQueryVariables,
  JobSharedThreeDFilesQuery,
  JobSharedThreeDFilesQueryVariables,
  Maybe,
  OrgManufacturerIdsQuery,
  OrgManufacturerIdsQueryVariables,
  ProfileQuery,
  ProfileQueryVariables,
  RefreshJwtMutation,
  RefreshJwtMutationVariables,
  SendRenderingShareEmailMutation,
  SendRenderingShareEmailMutationVariables,
  UpdateAiGeneratedImageMutation,
  UpdateAiGeneratedImageMutationVariables,
  UploadAiOriginalImageMutation,
  UploadAiOriginalImageMutationVariables,
} from 'src/shared/graphql/generated';
import {
  AuthenticateWithCookieDocument,
  CreateAiGeneratedImageAsyncDocument,
  CreateAiOriginalImageDocument,
  CreateAiOriginalImagePlaceholderDocument,
  CreateFileStorageTokenDocument,
  CreateHiDefRenderingDocument,
  CreateProjectDocument,
  DeleteAiOriginalImageDocument,
  DesignStudioProductDataDocument,
  DetectUserSessionDocument,
  GenericProductSettingsDocument,
  GetJobDocument,
  GetProductsDocument,
  GetRenderingsDocument,
  JobSharedThreeDFilesDocument,
  OrgManufacturerIdsDocument,
  ProfileDocument,
  RefreshJwtDocument,
  SendRenderingShareEmailDocument,
  UpdateAiGeneratedImageDocument,
  UploadAiOriginalImageDocument,
} from 'src/shared/graphql/generated';

interface ProductTypeSetting {
  disabled_types?: string[];
  disabled_colors?: string[];
  disabled_textures?: string[];
  disabled_texture_color_groups?: string[];
}

type GenericProductSettings = {
  [key: string]: ProductTypeSetting;
};

const queries = {
  detectUserSession: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: DetectUserSessionQueryVariables,
  ) =>
    client.query<DetectUserSessionQuery, DetectUserSessionQueryVariables>({
      query: DetectUserSessionDocument,
      variables,
    }),
  profile: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: ProfileQueryVariables,
  ) =>
    client.query<ProfileQuery, ProfileQueryVariables>({
      query: ProfileDocument,
      variables,
    }),
  orgManufacturers: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: OrgManufacturerIdsQueryVariables,
  ) =>
    client.query<OrgManufacturerIdsQuery, OrgManufacturerIdsQueryVariables>({
      query: OrgManufacturerIdsDocument,
      variables,
    }),
  getJob: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: GetJobQueryVariables,
  ) =>
    client.query<GetJobQuery, GetJobQueryVariables>({
      query: GetJobDocument,
      variables,
    }),
  getRenderings: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: GetRenderingsQueryVariables,
  ) =>
    client.query<GetRenderingsQuery, GetRenderingsQueryVariables>({
      query: GetRenderingsDocument,
      variables,
    }),
  getGenericProductSettings: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: GenericProductSettingsQueryVariables,
  ) => {
    const response = await client.query<
      GenericProductSettingsQuery,
      GenericProductSettingsQueryVariables
    >({
      query: GenericProductSettingsDocument,
      variables,
    });

    return response.data?.productCatalogConfigGenericProductSetting
      ?.settings as Maybe<GenericProductSettings>;
  },
  getMenuItems: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: DesignStudioProductDataQueryVariables,
  ) =>
    client.query<
      DesignStudioProductDataQuery,
      DesignStudioProductDataQueryVariables
    >({
      query: DesignStudioProductDataDocument,
      variables,
    }),
  getProducts: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: GetProductsQueryVariables,
  ) =>
    client.query<GetProductsQuery, GetProductsQueryVariables>({
      query: GetProductsDocument,
      variables,
    }),
  jobSharedThreeDFiles: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: JobSharedThreeDFilesQueryVariables,
  ) =>
    client.query<JobSharedThreeDFilesQuery, JobSharedThreeDFilesQueryVariables>(
      {
        query: JobSharedThreeDFilesDocument,
        variables,
      },
    ),
};

const mutations = {
  createProject: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: CreateProjectMutationVariables,
  ) =>
    client.mutate<CreateProjectMutation, CreateProjectMutationVariables>({
      mutation: CreateProjectDocument,
      variables,
    }),
  createStorageToken: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: CreateFileStorageTokenMutationVariables,
  ) =>
    client.mutate<
      CreateFileStorageTokenMutation,
      CreateFileStorageTokenMutationVariables
    >({ mutation: CreateFileStorageTokenDocument, variables }),
  deleteAiOriginalImage: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: DeleteAiOriginalImageMutationVariables,
  ) =>
    client.mutate<
      DeleteAiOriginalImageMutation,
      DeleteAiOriginalImageMutationVariables
    >({ mutation: DeleteAiOriginalImageDocument, variables }),
  sendRenderingShareEmail: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: SendRenderingShareEmailMutationVariables,
  ) =>
    client.mutate<
      SendRenderingShareEmailMutation,
      SendRenderingShareEmailMutationVariables
    >({ mutation: SendRenderingShareEmailDocument, variables }),
  refreshToken: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: RefreshJwtMutationVariables,
  ) =>
    client.mutate<RefreshJwtMutation, RefreshJwtMutationVariables>({
      mutation: RefreshJwtDocument,
      variables,
      context: { skipQueue: true },
    }),
  createHiDefRendering: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: CreateHiDefRenderingMutationVariables,
  ) =>
    client.mutate<
      CreateHiDefRenderingMutation,
      CreateHiDefRenderingMutationVariables
    >({ mutation: CreateHiDefRenderingDocument, variables }),
  createAiGeneratedImageAsync: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: CreateAiGeneratedImageAsyncMutationVariables,
  ) =>
    client.mutate<
      CreateAiGeneratedImageAsyncMutation,
      CreateAiGeneratedImageAsyncMutationVariables
    >({
      mutation: CreateAiGeneratedImageAsyncDocument,
      variables,
    }),
  createAiOriginalImagePlaceholder: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: CreateAiOriginalImagePlaceholderMutationVariables,
  ) =>
    client.mutate<
      CreateAiOriginalImagePlaceholderMutation,
      CreateAiOriginalImagePlaceholderMutationVariables
    >({
      mutation: CreateAiOriginalImagePlaceholderDocument,
      variables,
    }),
  createAiOriginalImage: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: CreateAiOriginalImageMutationVariables,
    update?: MutationUpdaterFn<CreateAiOriginalImageMutation>,
  ) =>
    client.mutate<
      CreateAiOriginalImageMutation,
      CreateAiOriginalImageMutationVariables
    >({
      mutation: CreateAiOriginalImageDocument,
      variables,
      update,
    }),
  updateAiGeneratedImage: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: UpdateAiGeneratedImageMutationVariables,
    optimisticResponse?: UpdateAiGeneratedImageMutation,
  ) =>
    client.mutate<
      UpdateAiGeneratedImageMutation,
      UpdateAiGeneratedImageMutationVariables
    >({
      mutation: UpdateAiGeneratedImageDocument,
      variables,
      optimisticResponse,
    }),
  authenticateWithCookie: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
  ) => {
    const { data } = await client.mutate<AuthenticateWithCookieMutation>({
      mutation: AuthenticateWithCookieDocument,
      context: {
        skipQueue: true,
        uri: `${config.GRAPHQL_URI}?time=${new Date().getTime()}`,
      },
      fetchPolicy: 'no-cache',
    });

    if (!data || !data.authenticateWithCookie) {
      return;
    }

    const {
      authenticateWithCookie: {
        jwt: { accessToken, refreshToken },
      },
    } = data;

    void defaultLocalStorageAdapter.setAccessToken(accessToken);
    void defaultLocalStorageAdapter.setRefreshToken(refreshToken);
  },
  uploadAiOriginalImage: async <TCacheShape = NormalizedCache>(
    client: ApolloClient<TCacheShape>,
    variables: UploadAiOriginalImageMutationVariables,
    update?: MutationUpdaterFn<UploadAiOriginalImageMutation>,
  ) =>
    client.mutate<UploadAiOriginalImageMutation>({
      mutation: UploadAiOriginalImageDocument,
      variables,
      update,
    }),
};

export { queries, mutations, GenericProductSettings };
